import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const FamilyContext = createContext();

const initialState = {
  todos: [],
  events: [],
  appointments: [],
  shoppingLists: [],
  shoppingItems: [],
  familyMembers: [],
  mealPlans: [],
  householdPlans: [],
  assets: [],
  memories: [],
  documents: [],
  emergencyContacts: [],
  medicalInfo: [],
  trashCalendar: [],
  financeTransactions: [],
  financeBudgets: [],
  packedBags: [],
  externalCalendars: [],
  externalEvents: [],
  settings: {
    darkMode: false,
    notifications: true,
  }
};

const toSnakeCase = (obj) => {
  const newObj = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    newObj[snakeKey] = obj[key];
  }
  return newObj;
};

const toCamelCase = (obj) => {
  const newObj = {};
  for (const key in obj) {
    const camelKey = key.replace(/([-_][a-z])/g, group =>
      group.toUpperCase().replace('-', '').replace('_', '')
    );
    newObj[camelKey] = obj[key];
  }
  return newObj;
};

const normalizeMeal = (m) => toCamelCase(m);
const normalizeMemory = (m) => toCamelCase(m);
const normalizeDocument = (d) => toCamelCase(d);
const normalizeMedical = (m) => toCamelCase(m);
const normalizeAppointment = (a) => toCamelCase(a);
const normalizeContact = (c) => toCamelCase(c);

function familyReducer(state, action) {
  const { type, payload } = action;
  switch (type) {
    case 'SET_INITIAL_DATA': 
      return { 
        ...state, 
        ...payload,
        mealPlans: (payload.mealPlans || []).map(normalizeMeal),
        memories: (payload.memories || []).map(normalizeMemory),
        documents: (payload.documents || []).map(normalizeDocument),
        medicalInfo: (payload.medicalInfo || []).map(normalizeMedical),
        appointments: (payload.appointments || []).map(normalizeAppointment),
        emergencyContacts: (payload.emergencyContacts || []).map(normalizeContact)
      };
    
    case 'ADD_TODO': return { ...state, todos: [payload, ...state.todos] };
    case 'UPDATE_TODO': return { ...state, todos: state.todos.map(t => t.id === payload.id ? payload : t) };
    case 'DELETE_TODO': return { ...state, todos: state.todos.filter(t => t.id !== payload) };
    
    case 'ADD_EVENT': return { ...state, events: [payload, ...state.events] };
    case 'UPDATE_EVENT': return { ...state, events: state.events.map(e => e.id === payload.id ? payload : e) };
    case 'DELETE_EVENT': return { ...state, events: state.events.filter(e => e.id !== payload) };

    case 'ADD_APPOINTMENT': return { ...state, appointments: [normalizeAppointment(payload), ...state.appointments] };
    case 'UPDATE_APPOINTMENT': return { ...state, appointments: state.appointments.map(a => a.id === payload.id ? normalizeAppointment(payload) : a) };
    case 'DELETE_APPOINTMENT': return { ...state, appointments: state.appointments.filter(a => a.id !== payload) };

    case 'ADD_SHOPPING_LIST': return { ...state, shoppingLists: [payload, ...state.shoppingLists] };
    case 'UPDATE_SHOPPING_LIST': return { ...state, shoppingLists: state.shoppingLists.map(l => l.id === payload.id ? payload : l) };
    case 'DELETE_SHOPPING_LIST': 
      return { 
        ...state, 
        shoppingLists: state.shoppingLists.filter(l => l.id !== payload),
        shoppingItems: state.shoppingItems.filter(i => i.list_id !== payload && i.listId !== payload)
      };
    case 'CLEAR_SHOPPING_LIST':
      return {
        ...state,
        shoppingItems: state.shoppingItems.filter(i => i.list_id !== payload && i.listId !== payload)
      };
    
    case 'ADD_SHOPPING_ITEM': return { ...state, shoppingItems: [payload, ...state.shoppingItems] };
    case 'UPDATE_SHOPPING_ITEM': return { ...state, shoppingItems: state.shoppingItems.map(i => i.id === payload.id ? payload : i) };
    case 'DELETE_SHOPPING_ITEM': return { ...state, shoppingItems: state.shoppingItems.filter(i => i.id !== payload) };

    case 'ADD_FAMILY_MEMBER': return { ...state, familyMembers: [...state.familyMembers, payload] };
    case 'UPDATE_FAMILY_MEMBER': return { ...state, familyMembers: state.familyMembers.map(m => m.id === payload.id ? payload : m) };
    case 'DELETE_FAMILY_MEMBER': return { ...state, familyMembers: state.familyMembers.filter(m => m.id !== payload) };
    
    case 'ADD_MEAL_PLAN': return { ...state, mealPlans: [normalizeMeal(payload), ...state.mealPlans] };
    case 'UPDATE_MEAL_PLAN': return { ...state, mealPlans: state.mealPlans.map(m => m.id === payload.id ? normalizeMeal(payload) : m) };
    case 'DELETE_MEAL_PLAN': return { ...state, mealPlans: state.mealPlans.filter(m => m.id !== payload) };
    
    case 'ADD_ASSET': return { ...state, assets: [payload, ...state.assets] };
    case 'UPDATE_ASSET': return { ...state, assets: state.assets.map(a => a.id === payload.id ? payload : a) };
    case 'DELETE_ASSET': return { ...state, assets: state.assets.filter(a => a.id !== payload) };
    
    case 'ADD_TRANSACTION': return { ...state, financeTransactions: [payload, ...state.financeTransactions] };
    case 'UPDATE_TRANSACTION': return { ...state, financeTransactions: state.financeTransactions.map(t => t.id === payload.id ? payload : t) };
    case 'DELETE_TRANSACTION': return { ...state, financeTransactions: state.financeTransactions.filter(t => t.id !== payload) };
    
    case 'ADD_BUDGET': return { ...state, financeBudgets: [...state.financeBudgets, payload] };
    case 'UPDATE_BUDGET': return { ...state, financeBudgets: state.financeBudgets.map(b => b.id === payload.id ? payload : b) };
    case 'DELETE_BUDGET': return { ...state, financeBudgets: state.financeBudgets.filter(b => b.id !== payload) };

    case 'ADD_TRASH_EVENT': return { ...state, trashCalendar: [payload, ...state.trashCalendar] };
    case 'UPDATE_TRASH_EVENT': return { ...state, trashCalendar: state.trashCalendar.map(e => e.id === payload.id ? payload : e) };
    case 'DELETE_TRASH_EVENT': return { ...state, trashCalendar: state.trashCalendar.filter(e => e.id !== payload) };

    case 'ADD_PACKED_BAG': return { ...state, packedBags: [payload, ...state.packedBags] };
    case 'UPDATE_PACKED_BAG': return { ...state, packedBags: state.packedBags.map(b => b.id === payload.id ? payload : b) };
    case 'DELETE_PACKED_BAG': return { ...state, packedBags: state.packedBags.filter(b => b.id !== payload) };

    case 'ADD_EMERGENCY_CONTACT': return { ...state, emergencyContacts: [normalizeContact(payload), ...state.emergencyContacts] };
    case 'UPDATE_EMERGENCY_CONTACT': return { ...state, emergencyContacts: state.emergencyContacts.map(c => c.id === payload.id ? normalizeContact(payload) : c) };
    case 'DELETE_EMERGENCY_CONTACT': return { ...state, emergencyContacts: state.emergencyContacts.filter(c => c.id !== payload) };

    case 'ADD_MEDICAL_INFO': return { ...state, medicalInfo: [normalizeMedical(payload), ...state.medicalInfo] };
    case 'UPDATE_MEDICAL_INFO': return { ...state, medicalInfo: state.medicalInfo.map(m => m.id === payload.id ? normalizeMedical(payload) : m) };
    case 'DELETE_MEDICAL_INFO': return { ...state, medicalInfo: state.medicalInfo.filter(m => m.id !== payload) };

    case 'ADD_MEMORY': return { ...state, memories: [normalizeMemory(payload), ...state.memories] };
    case 'UPDATE_MEMORY': return { ...state, memories: state.memories.map(m => m.id === payload.id ? normalizeMemory(payload) : m) };
    case 'DELETE_MEMORY': return { ...state, memories: state.memories.filter(m => m.id !== payload) };

    case 'ADD_DOCUMENT': return { ...state, documents: [normalizeDocument(payload), ...state.documents] };
    case 'UPDATE_DOCUMENT': return { ...state, documents: state.documents.map(d => d.id === payload.id ? normalizeDocument(payload) : d) };
    case 'DELETE_DOCUMENT': return { ...state, documents: state.documents.filter(d => d.id !== payload) };

    case 'ADD_HOUSEHOLD_PLAN': return { ...state, householdPlans: [payload, ...state.householdPlans] };
    case 'UPDATE_HOUSEHOLD_PLAN': return { ...state, householdPlans: state.householdPlans.map(h => h.id === payload.id ? payload : h) };
    case 'DELETE_HOUSEHOLD_PLAN': return { ...state, householdPlans: state.householdPlans.filter(h => h.id !== payload) };

    default: return state;
  }
}

export function FamilyProvider({ children }) {
  const [state, dispatch] = useReducer(familyReducer, initialState);
  const { user, familyId } = useAuth();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (user && familyId && !loadedRef.current) {
      loadFamilyData();
      loadedRef.current = true;
    }
  }, [user, familyId]);

  const loadFamilyData = async () => {
    try {
      const fetchers = [
        supabase.from('family_members').select('*').eq('family_id', familyId),
        supabase.from('todos').select('*').eq('family_id', familyId),
        supabase.from('events').select('*').eq('family_id', familyId),
        supabase.from('appointments').select('*').eq('family_id', familyId),
        supabase.from('shopping_lists').select('*').eq('family_id', familyId),
        supabase.from('shopping_items').select('*').eq('family_id', familyId),
        supabase.from('meal_plans').select('*').eq('family_id', familyId),
        supabase.from('packed_bags').select('*, items:packed_bag_items(*)').eq('family_id', familyId),
        supabase.from('finance_transactions').select('*').eq('family_id', familyId),
        supabase.from('finance_budgets').select('*').eq('family_id', familyId),
        supabase.from('assets').select('*').eq('family_id', familyId),
        supabase.from('trash_calendar').select('*').eq('family_id', familyId),
        supabase.from('emergency_contacts').select('*').eq('family_id', familyId),
        supabase.from('memories').select('*').eq('family_id', familyId),
        supabase.from('documents').select('*').eq('family_id', familyId),
        supabase.from('household_plans').select('*').eq('family_id', familyId),
        supabase.from('medical_info').select('*').eq('family_id', familyId)
      ];

      const results = await Promise.all(fetchers);
      const [members, todos, events, appointments, lists, items, meals, bags, transactions, budgets, assets, trash, emergency, memories, docs, household, medical] = results.map(r => r.data || []);

      dispatch({
        type: 'SET_INITIAL_DATA',
        payload: {
          familyMembers: members,
          todos,
          events,
          appointments,
          shoppingLists: lists,
          shoppingItems: items,
          mealPlans: meals,
          packedBags: bags,
          financeTransactions: transactions,
          financeBudgets: budgets,
          assets,
          trashCalendar: trash,
          emergencyContacts: emergency,
          memories: memories,
          documents: docs,
          householdPlans: household,
          medicalInfo: medical,
          externalCalendars: [],
          externalEvents: []
        }
      });
    } catch (error) {
      console.error('❌ Error loading family data:', error);
    }
  };

  const enhancedDispatch = async (action) => {
    const { type, payload } = action;
    if (!familyId || !user) {
      dispatch(action);
      return;
    }

    try {
      const tableMap = {
        'ADD_TODO': { table: 'todos', op: 'insert' },
        'UPDATE_TODO': { table: 'todos', op: 'update' },
        'DELETE_TODO': { table: 'todos', op: 'delete' },
        'ADD_EVENT': { table: 'events', op: 'insert' },
        'UPDATE_EVENT': { table: 'events', op: 'update' },
        'DELETE_EVENT': { table: 'events', op: 'delete' },
        'ADD_APPOINTMENT': { table: 'appointments', op: 'insert' },
        'UPDATE_APPOINTMENT': { table: 'appointments', op: 'update' },
        'DELETE_APPOINTMENT': { table: 'appointments', op: 'delete' },
        'ADD_SHOPPING_LIST': { table: 'shopping_lists', op: 'insert' },
        'UPDATE_SHOPPING_LIST': { table: 'shopping_lists', op: 'update' },
        'DELETE_SHOPPING_LIST': { table: 'shopping_lists', op: 'delete' },
        'ADD_SHOPPING_ITEM': { table: 'shopping_items', op: 'insert' },
        'UPDATE_SHOPPING_ITEM': { table: 'shopping_items', op: 'update' },
        'DELETE_SHOPPING_ITEM': { table: 'shopping_items', op: 'delete' },
        'ADD_FAMILY_MEMBER': { table: 'family_members', op: 'insert' },
        'UPDATE_FAMILY_MEMBER': { table: 'family_members', op: 'update' },
        'DELETE_FAMILY_MEMBER': { table: 'family_members', op: 'delete' },
        'ADD_MEAL_PLAN': { table: 'meal_plans', op: 'insert' },
        'UPDATE_MEAL_PLAN': { table: 'meal_plans', op: 'update' },
        'DELETE_MEAL_PLAN': { table: 'meal_plans', op: 'delete' },
        'ADD_ASSET': { table: 'assets', op: 'insert' },
        'UPDATE_ASSET': { table: 'assets', op: 'update' },
        'DELETE_ASSET': { table: 'assets', op: 'delete' },
        'ADD_TRANSACTION': { table: 'finance_transactions', op: 'insert' },
        'UPDATE_TRANSACTION': { table: 'finance_transactions', op: 'update' },
        'DELETE_TRANSACTION': { table: 'finance_transactions', op: 'delete' },
        'ADD_BUDGET': { table: 'finance_budgets', op: 'insert' },
        'UPDATE_BUDGET': { table: 'finance_budgets', op: 'update' },
        'DELETE_BUDGET': { table: 'finance_budgets', op: 'delete' },
        'ADD_TRASH_EVENT': { table: 'trash_calendar', op: 'insert' },
        'UPDATE_TRASH_EVENT': { table: 'trash_calendar', op: 'update' },
        'DELETE_TRASH_EVENT': { table: 'trash_calendar', op: 'delete' },
        'ADD_EMERGENCY_CONTACT': { table: 'emergency_contacts', op: 'insert' },
        'UPDATE_EMERGENCY_CONTACT': { table: 'emergency_contacts', op: 'update' },
        'DELETE_EMERGENCY_CONTACT': { table: 'emergency_contacts', op: 'delete' },
        'ADD_MEDICAL_INFO': { table: 'medical_info', op: 'insert' },
        'UPDATE_MEDICAL_INFO': { table: 'medical_info', op: 'update' },
        'DELETE_MEDICAL_INFO': { table: 'medical_info', op: 'delete' },
        'ADD_MEMORY': { table: 'memories', op: 'insert' },
        'UPDATE_MEMORY': { table: 'memories', op: 'update' },
        'DELETE_MEMORY': { table: 'memories', op: 'delete' },
        'ADD_DOCUMENT': { table: 'documents', op: 'insert' },
        'UPDATE_DOCUMENT': { table: 'documents', op: 'update' },
        'DELETE_DOCUMENT': { table: 'documents', op: 'delete' },
        'ADD_HOUSEHOLD_PLAN': { table: 'household_plans', op: 'insert' },
        'UPDATE_HOUSEHOLD_PLAN': { table: 'household_plans', op: 'update' },
        'DELETE_HOUSEHOLD_PLAN': { table: 'household_plans', op: 'delete' }
      };

      const map = tableMap[type];
      if (map) {
        if (map.op === 'insert') {
          let insertPayload = { 
            ...payload, 
            family_id: familyId, 
            id: payload.id || uuidv4(),
            created_by: user.id
          };

          if (map.table === 'shopping_items') {
            insertPayload.added_by = user.id;
          }
          
          let dbPayload = toSnakeCase(insertPayload);
          const { data, error } = await supabase.from(map.table).insert([dbPayload]).select().single();
          if (error) throw error;
          dispatch({ type, payload: data });
        } else if (map.op === 'update') {
          const dbPayload = toSnakeCase(payload);
          const { data, error } = await supabase.from(map.table).update(dbPayload).eq('id', payload.id).select().single();
          if (error) throw error;
          dispatch({ type, payload: data });
        } else if (map.op === 'delete') {
          await supabase.from(map.table).delete().eq('id', payload);
          dispatch(action);
        }
        return;
      }

      if (type === 'CLEAR_SHOPPING_LIST') {
        await supabase.from('shopping_items').delete().eq('list_id', payload);
        dispatch(action);
      } else if (type === 'ADD_PACKED_BAG') {
        const bagId = payload.id || uuidv4();
        const { data: bag, error: bagError } = await supabase.from('packed_bags').insert([{ 
          id: bagId, family_id: familyId, child_id: payload.child_id, destination: payload.destination, notes: payload.notes, created_by: user.id 
        }]).select().single();
        if (bagError) throw bagError;
        if (payload.items?.length > 0) {
          const { data: items } = await supabase.from('packed_bag_items').insert(payload.items.map(i => ({ 
            bag_id: bag.id, name: i.name, is_packed: true 
          }))).select();
          bag.items = items || [];
        } else bag.items = [];
        dispatch({ type, payload: bag });
      } else if (type === 'DELETE_PACKED_BAG') {
        await supabase.from('packed_bags').delete().eq('id', payload);
        dispatch(action);
      } else if (type === 'UPDATE_PACKED_BAG') {
        await supabase.from('packed_bags').update({ child_id: payload.child_id, destination: payload.destination, notes: payload.notes }).eq('id', payload.id);
        await supabase.from('packed_bag_items').delete().eq('bag_id', payload.id);
        const { data: items } = await supabase.from('packed_bag_items').insert(payload.items.map(i => ({ 
          bag_id: payload.id, name: i.name, is_packed: true, is_returned: i.is_returned 
        }))).select();
        dispatch({ type, payload: { ...payload, items } });
      } else {
        dispatch(action);
      }
    } catch (err) {
      console.error(`❌ Error in enhancedDispatch (${type}):`, err);
      dispatch(action);
    }
  };

  return (
    <FamilyContext.Provider value={{ state, dispatch: enhancedDispatch }}>
      {children}
    </FamilyContext.Provider>
  );
}

export const useFamilyContext = () => useContext(FamilyContext);